/**
 * 写真の顔・ナンバープレート自動ぼかし (docs/28) の型と判定ロジックの正本。
 *
 * - 地区設定 `District.photoPrivacy` (§8-1) は項目ごとに既定値を持ち、設定の無い既存地区も
 *   既定値 (自動ぼかし ON・元画像は無期限保存・地区管理者は閲覧可・エクスポート不可) で動く。
 * - 投稿の `Post.photoPrivacy` (§8-2) はサーバー (Cloud Functions) だけが書く。
 * - 時刻はすべて epoch ミリ秒 (collectionGroup クエリで `nextRetryAt <= now` を引くため数値で持つ)。
 */
export const PHOTO_PRIVACY_TARGETS = ['face', 'licensePlate'];
export function resolvePhotoPrivacySettings(raw) {
    const targets = (raw?.autoBlur?.targets ?? []).filter((t) => PHOTO_PRIVACY_TARGETS.includes(t));
    const days = raw?.keepOriginals?.retentionDays;
    const allowView = raw?.allowDistrictAdminViewOriginal !== false;
    return {
        autoBlurEnabled: raw?.autoBlur?.enabled !== false,
        targets: targets.length > 0 ? targets : [...PHOTO_PRIVACY_TARGETS],
        keepOriginals: raw?.keepOriginals?.enabled !== false,
        retentionDays: typeof days === 'number' && Number.isFinite(days) && days >= 1 ? Math.floor(days) : null,
        allowDistrictAdminViewOriginal: allowView,
        // 見られない地区ではエクスポートの許可も効かない (§5-5)。
        allowDistrictAdminOriginalExport: allowView && raw?.allowDistrictAdminOriginalExport === true,
    };
}
/** 検出処理の現在の版 (§4-8)。 */
export const PHOTO_PRIVACY_ENGINE_VERSION = 1;
/** 自動の再実行 (時間をおいた再実行) の上限回数と間隔 (§4-6)。 */
export const PHOTO_PRIVACY_RETRY_DELAYS_MS = [
    5 * 60_000,
    15 * 60_000,
    60 * 60_000,
    60 * 60_000,
];
export const PHOTO_PRIVACY_MAX_AUTO_RETRIES = PHOTO_PRIVACY_RETRY_DELAYS_MS.length;
export const PHOTO_PRIVACY_CATEGORY_LABELS = {
    none: '写真なし',
    unprocessed: '未処理',
    processing: '処理中',
    retrying: '失敗（自動で再実行予定）',
    'needs-action': '失敗（要対応）',
    withheld: '公開しない',
    detected: '検出あり',
    'not-detected': '検出なし',
    skipped: '対象外（自動ぼかしオフ）',
};
/** 自動の再実行を使い切った、または恒久的なエラー = 最終的な失敗 (§4-9)。 */
export function isPhotoPrivacyFinalFailure(p) {
    if (!p || p.status !== 'failed')
        return false;
    return p.failureKind === 'permanent' || (p.attempts ?? 0) > PHOTO_PRIVACY_MAX_AUTO_RETRIES;
}
export function photoPrivacyCategory(post) {
    const p = post.photoPrivacy;
    if (!p)
        return post.photoPath ? 'unprocessed' : 'none';
    switch (p.status) {
        case 'pending':
        case 'processing':
            return 'processing';
        case 'failed':
            return isPhotoPrivacyFinalFailure(p) ? 'needs-action' : 'retrying';
        case 'withheld':
            return 'withheld';
        case 'skipped':
            return 'skipped';
        case 'done': {
            const auto = p.detection?.autoRegionCount ?? 0;
            const manual = (p.regions ?? []).filter((r) => r.source === 'manual' && r.enabled).length;
            return auto + manual > 0 ? 'detected' : 'not-detected';
        }
        default:
            return 'unprocessed';
    }
}
/**
 * 写真をほかの参加者に見せてよいか (§4-3・§4-9)。処理が終わっていない・失敗・公開しないときは
 * 写真だけを出さない (本文は出す、D16)。`photoPrivacy` が無い投稿 (導入前) は従来どおり見せる。
 */
export function isPhotoPublished(post) {
    return isPhotoStatusPublished(post.photoPrivacy?.status, post.photoPrivacy?.publishedAt);
}
/** `isPhotoPublished` の状態だけ版 (状態が無い = 導入前の投稿は公開)。 */
export function isPhotoStatusPublished(status, publishedAt) {
    if (status == null || status === 'done' || status === 'skipped')
        return true;
    // 一度公開した写真は、編集・作り直しの処理中 (・失敗) も前のぼかし済みの写真を見せる。
    // 公開しない (withheld) は見せない。
    return status !== 'withheld' && typeof publishedAt === 'number';
}
/** 範囲の件数 (顔・ナンバー・手動) を数える。外した範囲は数えない。 */
export function countPhotoBlurRegions(regions) {
    const out = { face: 0, licensePlate: 0, manual: 0 };
    for (const r of regions ?? []) {
        if (!r.enabled)
            continue;
        out[r.kind === 'manual' ? 'manual' : r.kind]++;
    }
    return out;
}
/**
 * 本人専用の一時置き場 (§4-3)。参加者はここに写真を上げ、サーバーがぼかしてから
 * `districts/{d}/posts/{postId}/` に公開する。storage.rules / firestore.rules も同じ形を前提にする。
 */
export function incomingPhotoPath(districtId, uid, postId) {
    return `districts/${districtId}/incoming/${uid}/${postId}.jpg`;
}
/**
 * 一時置き場の写真のサムネ (本人の一覧・地図の表示用、端末が作って一緒に上げる)。
 * サーバーは処理しない (公開用のサムネはサーバーが作る) が、公開のあとで消す。
 */
export function incomingThumbPathOf(incomingPath) {
    return incomingPath.replace(/.jpg$/, '_thumb.jpg');
}
//# sourceMappingURL=photoPrivacy.js.map